import React, { useState } from "react";
import { toast } from "react-toastify";

/**
 * The DNS records a customer must publish to prove they own a domain
 * (Task 24333).
 *
 * Both `emaildomain/create` and `emaildomain/checkdns` hand back the same
 * record shape — `{ type, host, value, ttl?, status? }` — so this renders
 * either: the records to add straight after adding a domain, or the records
 * plus their per-record pass/fail state after a verification check.
 *
 * Props:
 *  - records:  array of { type, host, value, ttl, status }
 *  - showStatus: render the per-record status column (checkdns responses only)
 */
export default function DnsRecordsPanel({ records, showStatus = false }) {
  const [copied, setCopied] = useState(null);

  const list = Array.isArray(records) ? records : [];
  if (list.length === 0) return null;

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      toast.error("Could not copy — select the value and copy it manually.");
    }
  };

  return (
    <div>
      <p className="text-muted mb-2">
        Add these records at your DNS provider (the company that hosts your
        domain), then come back and press <strong>Verify</strong>. DNS changes
        can take up to 24–48 hours to propagate, though it is usually much
        quicker.
      </p>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead className="text-muted">
            <tr>
              <th style={{ width: 70 }}>Type</th>
              <th style={{ width: 200 }}>Host / Name</th>
              <th>Value</th>
              {showStatus && <th style={{ width: 90 }}>Status</th>}
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {list.map((record, index) => {
              const key = `${record?.host ?? ""}-${index}`;
              return (
                <tr key={key}>
                  <td>
                    <span className="badge bg-light text-muted border">
                      {record?.type || "TXT"}
                    </span>
                  </td>
                  <td className="text-break">{record?.host}</td>
                  <td
                    className="text-break"
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                  >
                    {record?.value}
                  </td>
                  {showStatus && (
                    <td>
                      {record?.status ? (
                        <span className="badge bg-success-subtle text-success">
                          Found
                        </span>
                      ) : (
                        <span className="badge bg-warning-subtle text-warning">
                          Pending
                        </span>
                      )}
                    </td>
                  )}
                  <td className="text-end">
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        copied === key ? "btn-success" : "btn-soft-secondary"
                      }`}
                      title="Copy value"
                      onClick={() => copy(record?.value, key)}
                    >
                      {copied === key ? (
                        "Copied"
                      ) : (
                        <i className="fa-solid fa-copy" />
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
